import React from "react";

// plugin imports
import { Link, Trans, useI18next } from "@herob191/gatsby-plugin-react-i18next";
import { motion } from "motion/react";
import { useLenis } from "lenis/react";

// style imports
import "./restaurantsAccordion.scss";

// component imports
import Separator from "../../spacers/separatorH";

// util imports
import useRestaurants from "../../../utils/restaurantsQuery";
import {
	getRestaurantCartaURL,
	getRestaurantReservaURL,
	getRestaurantURL,
} from "../../../utils/restaurant";
import useDayOfWeek, {
	formatHours,
	getHoursForRestaurantDate,
} from "../../../utils/date";
import useTranslate from "../../../utils/useTranslate";

// svg imports
import IconPlus from "../../../svg/icon/iconPlus.svg";
import IconStatus from "../../../svg/icon/iconStatus.svg";
import IconMarker from "../../../svg/icon/iconMarker.svg";

// component
function CityRestaurantContent({ restaurant }) {
	const [currentDay, setCurrentDay] = React.useState(new Date());

	React.useEffect(() => {
		const interval = setInterval(setCurrentDay(new Date()), 5 * 60 * 1000); // update every 5 minutes
		return () => clearInterval(interval);
	}, []);

	const ctaLinks = [
		{
			link: getRestaurantCartaURL(restaurant.fields.id),
			text: <Trans>Ver carta</Trans>,
		},
		{
			link: getRestaurantReservaURL(restaurant.fields.id),
			text: <Trans>Reservar</Trans>,
		},
	];

	return (
		<div>
			<Separator />
			<div className="padding cityRestaurantContent">
				<Link
					to={getRestaurantURL(restaurant.fields.id)}
					className="underlineHover"
				>
					<span className="h2">
						{useTranslate(restaurant.frontmatter.title)}
					</span>
				</Link>
				<div>
					<div className="contents">
						<div className="iconText">
							<IconStatus />
							<p>
								{useDayOfWeek(currentDay)}{" "}
								{formatHours(getHoursForRestaurantDate(restaurant, currentDay))}
							</p>
						</div>
						<a href={restaurant.frontmatter.gmapsLink} className="iconText">
							<IconMarker />
							<p className="underlineHover">{restaurant.frontmatter.address}</p>
						</a>
					</div>
					<div className="ctas">
						{ctaLinks.map((cta, i) => (
							<div key={"cta" + i}>
								<div className="tiltContainer borderThick invertOnHover">
									<Link to={cta.link}>
										<span className="h3">{cta.text}</span>
									</Link>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}

function CityRestaurantsAccordion({ location, restaurants }) {
	const { language } = useI18next();
	const lenis = useLenis();

	const contentRef = React.useRef();
	const [expanded, setExpanded] = React.useState(false);

	const city = location;
	const cityRestaurants = restaurants.filter(
		(restaurant) => restaurant.frontmatter.location[language] === location
	);

	React.useEffect(() => {
		if (expanded && contentRef && window && document) {
			const container = contentRef.current;
			const endScroll = window.scrollY + container.scrollHeight;

			lenis?.scrollTo(endScroll);
		}
	}, [contentRef, expanded, lenis]);

	return (
		<div className="cityRestaurantsAccordion">
			<button onClick={() => setExpanded(!expanded)}>
				<span className="h2 padding">
					<Trans>Locales {{ city }}</Trans>
				</span>
				<motion.div animate={{ rotate: expanded ? 45 : 0 }}>
					<IconPlus />
				</motion.div>
			</button>

			<motion.div
				initial={{ height: 0 }}
				animate={{ height: expanded ? "auto" : 0 }}
				className="cityRestaurantsContent"
				ref={contentRef}
			>
				{cityRestaurants.map((restaurant, i) => (
					<CityRestaurantContent
						restaurant={restaurant}
						key={location + "restaurant" + i}
					/>
				))}
			</motion.div>
		</div>
	);
}

export default function RestaurantsAccordion() {
	const { language } = useI18next();
	const restaurants = useRestaurants();
	const locations = [
		...new Set(
			restaurants.map((restaurant) => restaurant.frontmatter.location[language])
		),
	];

	return (
		<div className="restaurantsAccordion borderThick separateItems">
			{locations.map((location) => (
				<CityRestaurantsAccordion
					location={location}
					restaurants={restaurants}
					key={location}
				/>
			))}
		</div>
	);
}
